import React, { useState } from 'react';
import { CompleteKundali, HouseInfo, PlanetName, Language } from '../types/kundali';
import { Layers, Compass, Sparkles, Eye, Star, Award, BarChart2, ArrowUpRight } from 'lucide-react';
import { getPlanetShortCode, getPlanetFullName, getDignityName, getZodiacName, UI_TEXT } from '../utils/language';

interface HouseBreakdownViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onNavigateToHouse?: (houseNum: number) => void;
}

const NATURAL_HOUSE_KARAKAS: Record<number, { en: string; hi: string }> = {
  1: { en: 'Sun (Surya - Atma/Vitality)', hi: 'सूर्य (आत्मकारक / जीवन शक्ति)' },
  2: { en: 'Jupiter (Brihaspati - Wealth & Speech)', hi: 'गुरु (धन एवं वाणी कारक)' },
  3: { en: 'Mars (Mangal - Courage & Siblings)', hi: 'मंगल (पराक्रम एवं भ्राता कारक)' },
  4: { en: 'Moon & Venus (Chandra & Shukra - Mother & Comforts)', hi: 'चंद्र एवं शुक्र (मातृ एवं सुख कारक)' },
  5: { en: 'Jupiter (Brihaspati - Intellect & Children)', hi: 'गुरु (बुद्धि एवं संतान कारक)' },
  6: { en: 'Mars & Saturn (Mangal & Shani - Disease & Competition)', hi: 'मंगल एवं शनि (शत्रु एवं रोग कारक)' },
  7: { en: 'Venus (Shukra - Spouse & Partnership)', hi: 'शुक्र (कलत्र एवं विवाह कारक)' },
  8: { en: 'Saturn (Shani - Longevity & Transformation)', hi: 'शनि (आयु एवं गूढ़ कारक)' },
  9: { en: 'Jupiter & Sun (Brihaspati & Surya - Dharma & Fortune)', hi: 'गुरु एवं सूर्य (भाग्य एवं धर्म कारक)' },
  10: { en: 'Sun, Mercury & Saturn (Karma & Profession)', hi: 'सूर्य, बुध एवं शनि (कर्म कारक)' },
  11: { en: 'Jupiter (Brihaspati - Gains & Ambitions)', hi: 'गुरु (लाभ एवं सिद्धि कारक)' },
  12: { en: 'Saturn & Ketu (Moksha & Expenditure)', hi: 'शनि एवं केतु (मोक्ष एवं व्यय कारक)' },
};

export const HouseBreakdownView: React.FC<HouseBreakdownViewProps> = ({ kundali, lang = 'en', onNavigateToHouse }) => {
  const { houses, planets, ashtakvarga, shadbala } = kundali;
  const [selectedHouseNum, setSelectedHouseNum] = useState<number>(1);
  const [sortByStrength, setSortByStrength] = useState(false);
  const ui = UI_TEXT[lang];

  // Calculate most auspicious house (Highest SAV)
  const auspiciousHouse = [...houses].sort((a, b) => {
    const savA = ashtakvarga.sarvashtakvarga[a.houseNumber - 1] || 0;
    const savB = ashtakvarga.sarvashtakvarga[b.houseNumber - 1] || 0;
    return savB - savA;
  })[0];

  const auspiciousSav = ashtakvarga.sarvashtakvarga[auspiciousHouse.houseNumber - 1] || 0;

  const selectedHouse: HouseInfo = houses.find((h) => h.houseNumber === selectedHouseNum) || houses[0];
  const bhavaMeta = ui.houseMeanings[selectedHouseNum as keyof typeof ui.houseMeanings];
  const naturalKaraka = NATURAL_HOUSE_KARAKAS[selectedHouseNum]?.[lang] || NATURAL_HOUSE_KARAKAS[selectedHouseNum]?.en || 'Jupiter';

  // Get planets sitting in selected house
  const occupyingPlanets = planets.filter((p) => p.house === selectedHouseNum);

  // Get Lord of the selected house and where it sits
  const lordPlanetObj = planets.find((p) => p.name === selectedHouse.signLord);

  // Get Bhava Bala if available
  const houseBala = shadbala?.bhavaBala.find((b) => b.houseNumber === selectedHouseNum);

  // Get Ashtakvarga points for this house
  const savPoints = ashtakvarga.sarvashtakvarga[selectedHouseNum - 1] || 28;

  // Aspecting planets (Simulated/Calculated Parashari aspect lines)
  const aspectingPlanets = planets.filter((p) => {
    if (p.house === selectedHouseNum) return false;
    // 7th house direct aspect for all planets
    const dist = (selectedHouseNum - p.house + 12) % 12;
    if (dist === 6) return true;
    // Mars 4th and 8th aspect (dist 3 or 7)
    if (p.name === 'Mars' && (dist === 3 || dist === 7)) return true;
    // Jupiter 5th and 9th aspect (dist 4 or 8)
    if (p.name === 'Jupiter' && (dist === 4 || dist === 8)) return true;
    // Saturn 3rd and 10th aspect (dist 2 or 9)
    if (p.name === 'Saturn' && (dist === 2 || dist === 9)) return true;
    return false;
  });

  const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const houseBhinnaList = sevenPlanets.map((p) => ({
    planet: p,
    bindus: ashtakvarga.bhinna?.[p]?.[selectedHouseNum - 1] ?? 0
  }));
  
  const status = savPoints >= 32 ? 'exceptional' : savPoints >= 28 ? 'strong' : savPoints >= 25 ? 'moderate' : 'low';
  
  const getSavStatusText = () => {
    if (lang === 'hi') {
        switch(status) {
            case 'exceptional': return 'असाधारण शक्ति (32+ अंक)। प्राकृतिक प्रचुरता, श्रम का सहज फल और ट्रांजिट के दौरान उच्च लचीलापन।';
            case 'strong': return 'मजबूत और शुभ (28–31 अंक)। 28 की सीमा से अधिक। निरंतर भौतिक और व्यक्तिगत विकास को बढ़ावा देता है।';
            case 'moderate': return 'मध्यम आधार रेखा (25–27 अंक)। सचेत योजना, दृढ़ता और अनुकूल दशा अवधि की आवश्यकता है।';
            default: return 'कम जीवन शक्ति (<25 अंक)। सचेत प्रबंधन, भाव स्वामी को मजबूत करने और अशुभ ट्रांजिट के दौरान उपचारात्मक देखभाल की मांग करता है।';
        }
    }
    return savPoints >= 32
      ? 'Exceptional strength (32+ pts). Natural abundance, effortless fruit of labor, and superior resilience during transits.'
      : savPoints >= 28
      ? 'Strong & auspicious (28–31 pts). Dependable baseline exceeding the 28 threshold. Fosters consistent material and personal growth.'
      : savPoints >= 25
      ? 'Moderate baseline (25–27 pts). Steady results requiring conscious planning, perseverance, and favorable dasha periods.'
      : 'Lower vitality (<25 pts). Demands mindful stewardship, strengthening of house lord, and remedial care during malefic transits.';
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-astro-primary/10 border border-astro-primary/20 text-astro-primary font-bold flex items-center justify-center text-xl shadow-sm font-serif">
            <Layers className="w-6 h-6 text-astro-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-astro-text font-serif">{lang === 'hi' ? '12 भाव (घर) विश्लेषण' : 'Comprehensive 12 Bhavas (Houses) Breakdown'}</h2>
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2 py-0.5 rounded-md">
                {lang === 'hi' ? 'पराशरी वैदिक विश्लेषण' : 'Parashari Vedic Analysis'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {lang === 'hi' 
                ? 'भावों, स्वामियों, बैठकों, पहलुओं, भाव बल और अष्टकवर्ग स्कोर का विस्तृत भाव-दर-भाव विश्लेषण।'
                : 'Detailed house-by-house analysis of lords, occupants, aspects, Bhava Bala, and Ashtakvarga scores.'}
            </p>
          </div>
        </div>

        {/* Most Auspicious House Banner */}
        <div 
          onClick={() => setSelectedHouseNum(auspiciousHouse.houseNumber)}
          className="cursor-pointer group flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:px-5 transition-all hover:bg-emerald-100 hover:border-emerald-300 shadow-sm animate-in fade-in zoom-in duration-500"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
              <Star className="w-5 h-5 fill-current" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-tighter">
                {lang === 'hi' ? 'सबसे शुभ भाव' : 'Most Auspicious House'}
              </div>
              <div className="text-sm font-bold text-astro-primary font-serif">
                House {auspiciousHouse.houseNumber}: {ui.houseMeanings[auspiciousHouse.houseNumber as keyof typeof ui.houseMeanings]?.name}
              </div>
              <div className="text-[10px] text-emerald-700 font-medium">
                {lang === 'hi' ? `सर्वाष्टकवर्ग: ${auspiciousSav} बिंदु (असाधारण शक्ति)` : `Sarvashtakvarga: ${auspiciousSav} Bindus (Exceptional Vitality)`}
              </div>
            </div>
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToHouse?.(auspiciousHouse.houseNumber);
            }}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            {lang === 'hi' ? 'डीप डाइव देखें' : 'Inspect Deep Dive'}
            <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* 12 House Selector Navigation Tabs */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-sm">
        <div className="text-xs font-bold text-astro-primary uppercase tracking-wider mb-2 font-serif flex items-center gap-1.5">
          <Compass className="w-4 h-4 text-astro-primary" /> {lang === 'hi' ? 'विश्लेषण के लिए भाव चुनें (1 से 12)' : 'Select House to Inspect (1 to 12)'}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
          {houses.map((h) => {
            const isSelected = h.houseNumber === selectedHouseNum;
            const houseSav = ashtakvarga.sarvashtakvarga[h.houseNumber - 1] || 28;

            return (
              <button
                key={h.houseNumber}
                onClick={() => setSelectedHouseNum(h.houseNumber)}
                className={`p-2 rounded-xl text-center border transition-all ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary font-bold shadow-md scale-105 ring-2 ring-astro-primary/20 animate-pulse'
                    : 'bg-astro-card border-astro-border text-astro-secondary-text hover:text-astro-text hover:border-astro-primary/40'
                }`}
              >
                <div className="text-xs font-bold font-mono">H{h.houseNumber}</div>
                <div className={`text-[10px] font-serif ${isSelected ? 'text-white/90' : 'text-astro-primary font-bold'}`}>
                  R{h.signIndex + 1} {h.sign.slice(0, 3)} ({h.signLord.slice(0, 3)})
                </div>
                <div className={`text-[9px] font-mono mt-0.5 font-bold ${isSelected ? 'text-astro-card' : 'text-astro-accent'}`}>
                  SAV {houseSav}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Selected House Detail Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Main House Specs & Analysis */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main House Banner Card */}
          <div 
            key={selectedHouseNum}
            className="bg-astro-bg border border-astro-primary/30 rounded-2xl p-6 shadow-sm space-y-4 animate-in fade-in slide-in-from-left-4 duration-500 ring-2 ring-transparent transition-all active:ring-astro-primary/20"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
              <div>
                <span className="text-[10px] font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full uppercase">
                  {lang === 'hi' ? 'भाव' : 'House'} {selectedHouse.houseNumber} • {bhavaMeta.name}
                </span>
                <h3 className="text-xl font-bold text-astro-text font-serif mt-1">
                  {bhavaMeta.name}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">{bhavaMeta.desc}</p>
                
                <button 
                  onClick={() => onNavigateToHouse?.(selectedHouse.houseNumber)}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1 bg-astro-primary text-white text-[10px] font-bold rounded-lg hover:bg-astro-text transition-colors shadow-sm"
                >
                  {lang === 'hi' ? 'चार्ट में देखें' : 'View in Chart Inspector'}
                  <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>

              <div className="text-right shrink-0 bg-astro-card px-4 py-2.5 rounded-xl border border-astro-border">
                <span className="text-[10px] uppercase text-astro-secondary-text block font-semibold">{lang === 'hi' ? 'राशि व स्वामी' : 'Rashi & Lord'}</span>
                <span className="text-base font-bold text-astro-primary font-serif">R{selectedHouse.signIndex + 1} {selectedHouse.sign} • Lord: {selectedHouse.signLord}</span>
                <span className="text-[10px] text-astro-secondary-text block">{lang === 'hi' ? 'शासक ग्रह' : 'Ruling Planet'}</span>
              </div>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                <span className="text-astro-secondary-text text-[10px] uppercase block">{lang === 'hi' ? 'स्वामी की स्थिति' : 'House Lord Placement'}</span>
                <span className="font-bold text-astro-primary flex items-center gap-1.5 flex-wrap">
                  {getPlanetFullName(selectedHouse.signLord as any, lang)} in H{lordPlanetObj?.house || '?'} ({getZodiacName(lordPlanetObj?.sign, lang)})
                  {(() => {
                    const md = kundali.dashas.find(d => d.isCurrent);
                    const ad = md?.antardashas?.find(ad => ad.isCurrent);
                    const pd = ad?.antardashas?.find(pd => pd.isCurrent);
                    
                    if (md?.planet === selectedHouse.signLord) return <span className="text-[8px] bg-astro-primary text-white px-1.5 py-0.2 rounded-full font-bold">MD</span>;
                    if (ad?.planet === selectedHouse.signLord) return <span className="text-[8px] bg-astro-primary/20 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/30">AD</span>;
                    if (pd?.planet === selectedHouse.signLord) return <span className="text-[8px] bg-astro-primary/10 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/20">PD</span>;
                    return null;
                  })()}
                </span>
                <span className="text-[10px] text-astro-secondary-text block">{getDignityName(lordPlanetObj?.dignity || 'Neutral', lang)}</span>
              </div>

              <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                <span className="text-astro-secondary-text text-[10px] uppercase block">{lang === 'hi' ? 'सर्वाष्टकवर्ग (SAV अंक)' : 'Sarvashtakvarga (SAV)'}</span>
                <span className="font-bold font-mono text-base text-astro-accent">{savPoints} / 56 Bindus</span>
                <span className={`text-[10px] block ${savPoints >= 28 ? 'text-astro-primary font-semibold' : 'text-rose-600'}`}>
                  {savPoints >= 28 ? (lang === 'hi' ? 'शुभ (28+)' : 'Strong Benefic (28+)') : (lang === 'hi' ? 'उपाय आवश्यक (<28)' : 'Needs Remedial Focus (<28)')}
                </span>
              </div>

              <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                <span className="text-astro-secondary-text text-[10px] uppercase block">{lang === 'hi' ? 'भाव बल' : 'Bhava Bala Strength'}</span>
                <span className="font-bold font-mono text-base text-astro-primary">
                  {houseBala ? `${houseBala.totalRupas} R` : 'Calculated'}
                </span>
                <span className="text-[10px] text-astro-secondary-text block">{houseBala?.category || 'Moderate'}</span>
              </div>

              <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                <span className="text-astro-secondary-text text-[10px] uppercase block">{lang === 'hi' ? 'प्राकृतिक कारक' : 'Natural Karaka'}</span>
                <span className="font-bold text-astro-text text-xs">{naturalKaraka}</span>
                <span className="text-[10px] text-astro-secondary-text block">{lang === 'hi' ? 'कारक' : 'Significator'}</span>
              </div>
            </div>

            {/* Ashtakavarga Deep-Dive Evaluation Card */}
            <div className="p-4 bg-astro-bg rounded-xl border border-astro-accent/35 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border pb-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-astro-accent/15 text-astro-accent font-bold flex items-center justify-center text-xs font-serif">
                    AV
                  </span>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5 font-serif">
                    <BarChart2 className="w-3.5 h-3.5 text-astro-accent" /> {lang === 'hi' ? 'अष्टकवर्ग मूल्यांकन' : 'Ashtakvarga Evaluation'}
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    savPoints >= 32
                      ? 'bg-emerald-100 text-emerald-800'
                      : savPoints >= 28
                      ? 'bg-teal-100 text-teal-800'
                      : savPoints >= 25
                      ? 'bg-astro-secondary/15 text-astro-secondary'
                      : 'bg-rose-100 text-rose-800'
                  }`}>
                    {status === 'exceptional' ? (lang === 'hi' ? 'असाधारण (32+)' : 'Exceptional (32+)') : status === 'strong' ? (lang === 'hi' ? 'मजबूत (28-31)' : 'Strong (28-31)') : status === 'moderate' ? (lang === 'hi' ? 'मध्यम (25-27)' : 'Moderate (25-27)') : (lang === 'hi' ? 'कम (<25)' : 'Low (<25)')}
                  </span>
                  <span className="font-mono text-xs font-bold text-astro-primary bg-astro-card px-2 py-0.5 rounded border border-astro-border">
                    {savPoints} / 56 {ui.points}
                  </span>
                </div>
              </div>

              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                {getSavStatusText()}
              </p>

              {/* BAV 7 Graha Grid */}
              <div className="space-y-1.5 pt-1 border-t border-astro-border/70">
                <div className="flex items-center justify-between text-[10px] text-astro-secondary-text">
                  <span className="font-semibold text-astro-text">{lang === 'hi' ? 'भिन्न अष्टकवर्ग (BAV) योगदान:' : 'Bhinna Ashtakavarga (BAV) Contribution:'}</span>
                  <span>{lang === 'hi' ? 'मानक: 4+ बिंदु = शुभ ट्रांजिट' : 'Benchmark: 4+ Bindus = Auspicious transit support'}</span>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {houseBhinnaList.map((item) => (
                    <div
                      key={item.planet}
                      className={`p-1.5 rounded-lg border text-center ${
                        item.bindus >= 5
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-900 font-bold'
                          : item.bindus === 4
                          ? 'bg-astro-card border-astro-border text-astro-text'
                          : 'bg-rose-50 border-rose-200 text-rose-900'
                      }`}
                    >
                      <span className="text-[10px] font-bold block">{getPlanetShortCode(item.planet, lang)}</span>
                      <span className="text-xs font-mono">{item.bindus}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Occupying Planets Section */}
            <div className="space-y-2 pt-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                <Star className="w-4 h-4 text-astro-accent" /> {lang === 'hi' ? 'भाव में स्थित ग्रह' : 'Occupying Planets in House'} {selectedHouseNum} ({occupyingPlanets.length})
              </h4>

              {occupyingPlanets.length === 0 ? (
                <div className="p-3 bg-astro-card rounded-xl border border-astro-border text-xs text-astro-secondary-text italic">
                  {lang === 'hi' ? 'कोई ग्रह सीधे इस भाव में नहीं है।' : 'No planets sit directly in House.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {occupyingPlanets.map((p) => (
                    <div key={p.name} className="p-3 bg-astro-card rounded-xl border border-astro-border space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-astro-primary flex items-center gap-1.5 flex-wrap">
                          {getPlanetFullName(p.name, lang)} {p.isRetrograde ? (lang === 'hi' ? '(वक्री)' : '(R)') : ''}
                          {(() => {
                            const md = kundali.dashas.find(d => d.isCurrent);
                            const ad = md?.antardashas?.find(ad => ad.isCurrent);
                            const pd = ad?.antardashas?.find(pd => pd.isCurrent);
                            
                            if (md?.planet === p.name) return <span className="text-[8px] bg-astro-primary text-white px-1.5 py-0.2 rounded-full font-bold">MD</span>;
                            if (ad?.planet === p.name) return <span className="text-[8px] bg-astro-primary/20 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/30">AD</span>;
                            if (pd?.planet === p.name) return <span className="text-[8px] bg-astro-primary/10 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/20">PD</span>;
                            return null;
                          })()}
                        </span>
                        <span className="font-mono text-[11px] text-astro-text">{p.degreeFormatted}</span>
                      </div>
                      <div className="text-[10px] text-astro-secondary-text flex items-center justify-between">
                        <span>{lang === 'hi' ? 'नक्षत्र' : 'Nakshatra'}: {p.nakshatra} ({lang === 'hi' ? `पद ${p.pada}` : `Pada ${p.pada}`})</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border inline-flex items-center gap-0.5 ${
                          p.dignity === 'Exalted' ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                          p.dignity === 'Debilitated' ? 'bg-rose-100 text-rose-900 border-rose-300' :
                          p.dignity === 'Own House' ? 'bg-teal-50 text-astro-primary border-teal-300' :
                          'bg-astro-bg text-astro-secondary-text border-astro-border'
                        }`}>
                          {p.dignity === 'Exalted' && '↑ '}
                          {p.dignity === 'Debilitated' && '↓ '}
                          {getDignityName(p.dignity, lang)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Aspecting Planets Section */}
            <div className="space-y-2 pt-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-astro-accent" /> {lang === 'hi' ? 'ग्रह दृष्टि' : 'Planetary Aspects (Drishti)'} on House {selectedHouseNum} ({aspectingPlanets.length})
              </h4>

              {aspectingPlanets.length === 0 ? (
                <div className="p-3 bg-astro-card rounded-xl border border-astro-border text-xs text-astro-secondary-text italic">
                  {lang === 'hi' ? 'कोई ग्रह सीधे दृष्टि नहीं डाल रहा।' : 'No full Parashari aspecting planets cast direct eye on House.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {aspectingPlanets.map((ap) => (
                    <div key={ap.name} className="p-2.5 bg-astro-card rounded-xl border border-astro-border flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-astro-primary" />
                        <span className="font-bold text-astro-text">{ap.name}</span>
                        <span className="text-[10px] text-astro-secondary-text">{lang === 'hi' ? 'भाव' : 'from House'} {ap.house} ({ap.sign})</span>
                      </div>
                      <span className="text-[10px] font-semibold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded border border-astro-primary/20">
                        {lang === 'hi' ? 'सक्रिय दृष्टि' : 'Direct Aspect'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right 1 Col: All 12 Houses Quick Summary Table */}
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <h3 className="font-bold text-astro-primary font-serif text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-astro-accent" /> {lang === 'hi' ? '12 भाव सारांश' : '12 Houses Strength Snapshot'}
            </h3>
            <button
              onClick={() => setSortByStrength(!sortByStrength)}
              className={`p-1.5 rounded-lg border transition-all ${
                sortByStrength 
                  ? 'bg-astro-primary text-white border-astro-primary' 
                  : 'bg-astro-card border-astro-border text-astro-secondary-text hover:border-astro-primary'
              }`}
              title="Sort by Beneficial Strength (Descending)"
            >
              <BarChart2 className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2 overflow-y-auto max-h-[500px] pr-1">
            {(sortByStrength 
              ? [...houses].sort((a, b) => {
                  const scoreA = ashtakvarga.sarvashtakvarga[a.houseNumber - 1] || 28;
                  const scoreB = ashtakvarga.sarvashtakvarga[b.houseNumber - 1] || 28;
                  return scoreB - scoreA;
                })
              : houses
            ).map((h) => {
              const hSav = ashtakvarga.sarvashtakvarga[h.houseNumber - 1] || 28;
              const hPlanets = planets.filter((p) => p.house === h.houseNumber);
              const isSelected = h.houseNumber === selectedHouseNum;

              return (
                <div
                  key={h.houseNumber}
                  onClick={() => setSelectedHouseNum(h.houseNumber)}
                  className={`p-3 rounded-xl border text-xs cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-astro-primary/10 border-astro-primary text-astro-text'
                      : 'bg-astro-card border-astro-border text-astro-secondary-text hover:border-astro-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-astro-primary">
                      H{h.houseNumber}: {ui.houseMeanings[h.houseNumber as keyof typeof ui.houseMeanings]?.name || 'Bhava'}
                    </span>
                    <span className="font-mono text-astro-text font-bold">{hSav} {ui.points}</span>
                  </div>

                  <div className="text-[10px] text-astro-secondary-text mt-1 flex justify-between">
                    <span>{lang === 'hi' ? 'राशि' : 'Sign'}: {h.sign} ({lang === 'hi' ? 'स्वामी' : 'Lord'}: {h.signLord})</span>
                    <span>{hPlanets.length > 0 ? `${hPlanets.length} ${lang === 'hi' ? 'ग्रह' : 'Planet(s)'}` : (lang === 'hi' ? 'खाली' : 'Empty')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
